import { usePlayVideoState } from 'components/discover/contexts/PlayVideoContext';
import { SpCarousel } from 'components/discover/publications/carousel';
import { PublicationEmpty, PublicationError } from 'components/discover/publications/placeholder';
import { useFetchArrayData } from 'hooks/useFetchData';
import type { IntlKey } from 'locales';
import { type ReactNode, useCallback, useContext, useState } from 'react';
import { Modal, ModalBody } from 'react-bootstrap';
import type { Observable } from 'rxjs';

import { RegionContext } from '../contexts/RegionContext';
import type { PublicationItem } from './model';
import { type PublicationSharedProps } from './publication';

interface PublicationContainerProps {
  skeleton: ReactNode;
  seeMoreModalContent?: (onClose: () => void) => ReactNode;

  noConsent?: ReactNode;
  soonKey: IntlKey;
  errorKey: IntlKey;
  id: string;
  api: (region: string) => Observable<ReadonlyArray<PublicationItem>>;
  seeMoreModalE2e?: string;
  hasGivenConsent?: boolean;
}

export function PublicationContainer({
  api,
  seeMoreModalE2e,
  id,
  hasGivenConsent = false,
  noConsent,
  errorKey,
  soonKey,
  skeleton,
  seeMoreKey,
  onSeeMore,
  seeMoreModalContent,
  trackOpen,
  ...otherKeys
}: PublicationContainerProps & PublicationSharedProps) {
  const [isSeeMoreOpened, setIsSeeMoreOpened] = useState(false);
  const hasSeeMore: boolean = seeMoreKey !== undefined && onSeeMore !== undefined && seeMoreModalContent !== undefined;

  const playVideoState = usePlayVideoState();

  const isModalOpened = isSeeMoreOpened || playVideoState?.isPlaying === true;

  const onOpenSeeMore = useCallback(() => {
    setIsSeeMoreOpened(true);
    if (onSeeMore) {
      onSeeMore();
    }
  }, [onSeeMore]);

  const onCloseSeeMore = useCallback(() => {
    setIsSeeMoreOpened(false);
    if (playVideoState?.isPlaying === true) {
      playVideoState.close();
    }
  }, [playVideoState]);

  const toggleSeeOpened = useCallback(() => {
    if (isModalOpened) {
      onCloseSeeMore();
    } else {
      onOpenSeeMore();
    }
  }, [isModalOpened, onCloseSeeMore, onOpenSeeMore]);

  const { region } = useContext(RegionContext);

  const fetchPublications = useCallback(() => api(region), [api, region]);

  const [status, items] = useFetchArrayData<PublicationItem>(fetchPublications);
  if (noConsent !== undefined) {
    if (hasGivenConsent === undefined) {
      return <>{skeleton}</>;
    }
    if (hasGivenConsent === false) {
      return <>{noConsent}</>;
    }
  }

  switch (status) {
    case 'success':
      return (
        <>
          <SpCarousel
            id={id}
            titleKey={otherKeys.titleKey}
            items={items!}
            onSeeMore={onOpenSeeMore}
            stayStill={isSeeMoreOpened}
            seeMoreLabelId={seeMoreKey}
            onTrackOpen={trackOpen}
          />
          {hasSeeMore && (
            <Modal show={isModalOpened} onHide={toggleSeeOpened} centered size="xl" backdrop={true}>
              <ModalBody className="p-0" data-e2e={seeMoreModalE2e}>
                {seeMoreModalContent?.(onCloseSeeMore)}
              </ModalBody>
            </Modal>
          )}
        </>
      );
    case 'error':
    case 'unauthorized':
      return <PublicationError id={id} errorKey={errorKey} />;
    case 'empty':
      return id === 'thematics' ? null : <PublicationEmpty id={id} soonKey={soonKey} />;
    case 'loading':
      return id === 'thematics' ? null : <>{skeleton}</>;
  }
}
